import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { videoURL } from 'src/app/services/base-url';
import { TvProductionTrackerService } from 'src/app/services/tv-production-tracker.service';

@Component({
  selector: 'app-publishing-workflow',
  templateUrl: './publishing-workflow.component.html',
  styleUrls: ['./publishing-workflow.component.css']
})
export class PublishingWorkflowComponent implements OnInit {

  statusMap: any = {
    'CREATED': 'Created',
    'EDITOR_ASSIGNED': 'Editor Assigned',
    'EDIT_COMPLETED': 'Edit Completed',
    'TITLE_ASSIGNED': 'Title Assigned',
    'TITLE_COMPLETED': 'Title Completed',
    'GRAPHICS_ASSIGNED': 'Graphics Assigned',
    'GRAPHICS_COMPLETED': 'Graphics Completed',
    'SEO_ASSIGNED': 'SEO Assigned',
    'SEO_COMPLETED': 'SEO Completed',
    'QUALITY_CHECK_ASSIGNED': 'Quality Check Assigned',
    'QUALITY_CHECK_COMPLETED': 'Quality Check Completed',
    'PUBLISHING_ASSIGNED': 'Publishing Assigned',
    'PUBLISHING_COMPLETED': 'Publishing Completed',
    'COMPLETED': 'Completed',
    'PENDING': 'Pending',
    'EPISODE_CREATION': 'Episode Creation'
  };

  episodeList: any = [];
  pageNumber: number = 1;
  totalRecords: number = 0;
  first: number = 0;
  tableSize: number = 10;
  isLoading: boolean = false;
  searchFilter: string = "";

  selectedEpisode: any = null;
  singleEpisodeData: any = [];
  workflowSteps = [
    { stage: 'EPISODE_CREATION', label: 'Shoot' },
    { stage: 'EDITOR', label: 'Edit' },
    { stage: 'GRAPHICS', label: 'Graphics' },
    { stage: 'TITLE', label: 'Title' },
    { stage: 'QUALITY_CHECK', label: 'Quality Check' },
    { stage: 'SEO', label: 'SEO' },
    { stage: 'COMPLETED', label: 'Completed' }
  ];
  pubForm: any | FormGroup;
  formSubmit: boolean = false;
  cid: any;
  loggedInUserId: any;
  videoUrl: any = videoURL


  constructor(
    private tvProductionTrackerService: TvProductionTrackerService,
    private toastr: ToastrService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.cid = Number(sessionStorage.getItem('cid'));
    this.loggedInUserId = Number(sessionStorage.getItem('userId')) || 0;
    this.initForm();
    this.getAllEpisodes();
  }

  initForm() {
    this.pubForm = this.fb.group({
      commonNotes: ['']
    });
  }

  getAllEpisodes() {
    this.isLoading = true;
    let body: any = {
      "episodeStatus": "COMPLETED"
    }
    this.tvProductionTrackerService.filterByEpisodeStatus(body).subscribe(
      (res: any) => {
        if (res && res.response) {
          this.episodeList = res.response;
          this.totalRecords = this.episodeList.length;
        } else {
          this.episodeList = [];
          this.totalRecords = 0;
        }
        this.isLoading = false;
      },
      (err: any) => {
        console.error(err);
        this.isLoading = false;
      }
    );
  }

  onTableDataChange(event: any) {
    this.pageNumber = event.page + 1;
  }

  refreshAll() {
    this.pageNumber = 1;
    this.first = 0;
    this.searchFilter = "";
    this.getAllEpisodes();
  }

  openPubModal(episode: any) {
    this.selectedEpisode = episode;
    this.pubForm.patchValue({
      commonNotes: ''
    });
    this.formSubmit = false;
  }

  closeModal() {
    document.getElementById("closePubModalBtn")?.click();
    this.selectedEpisode = null;
    this.pubForm.reset();
  }

  saveDetails(complete: boolean) {
    if (this.pubForm.invalid) {
      this.formSubmit = true;
      this.toastr.error("Please fill required fields");
      return;
    }

    let commonNotesArr = this.selectedEpisode.commonNotes || [];
    if (this.pubForm.get('commonNotes').value) {
      commonNotesArr.push({
        userId: Number(sessionStorage.getItem('userId')) || 0,
        userName: sessionStorage.getItem('userName') || '',
        commonNotes: this.pubForm.get('commonNotes').value,
        date: new Date().getTime()
      });
    }

    let nextStage = this.selectedEpisode.currentStage;
    if (complete) {
      nextStage = "COMPLETED";
    }

    let finalEndDate = this.selectedEpisode.publishingEndDate || 0;
    if (complete) {
      finalEndDate = new Date().getTime();
    }

    const payload: any = {
      ...this.selectedEpisode,
      publishingEndDate: finalEndDate,
      commonNotes: commonNotesArr,
      episodeStatus: complete ? 'COMPLETED' : this.selectedEpisode.episodeStatus,
      currentStage: nextStage,
      cid: this.cid
    };
    delete payload.episodeNotes;
    delete payload.editorNotes;
    delete payload.titleNotes;
    delete payload.graphicsNotes;
    delete payload.seoNotes;
    delete payload.qualityCheckNotes;
    delete payload.publishingNotes;

    this.tvProductionTrackerService.updateEpisodeWorkflow(payload).subscribe(
      (res: any) => {
        this.toastr.success(res.message || 'Workflow updated successfully');
        this.getAllEpisodes();
        this.closeModal();
      },
      (err: any) => {
        this.toastr.error(err?.error?.errors?.[0]?.description || 'Error updating workflow');
      }
    );
  }

  openViewModal(episode: any) {
    if (episode && (episode.tvProductionTrackerId || episode.id)) {
      const id = episode.tvProductionTrackerId || episode.id;
      this.tvProductionTrackerService.getEpisodeById(id).subscribe(
        (res: any) => {
          this.singleEpisodeData = [res.response || episode];
        },
        (err: any) => {
          console.error(err);
          this.singleEpisodeData = [episode];
        }
      );
    } else {
      this.singleEpisodeData = [episode];
    }
  }

  closeViewModal() {
    document.getElementById("closeViewModalButton")?.click();
    this.singleEpisodeData = [];
  }

  closePopUp() {
    this.closeViewModal();
  }

  isStageCompleted(currentStage: string, stepStage: string): boolean {
    if (!currentStage) currentStage = 'EPISODE_CREATION';
    if (stepStage === 'COMPLETED') {
      return currentStage === 'COMPLETED';
    }
    const stageOrder = ['EPISODE_CREATION', 'EDITOR', 'GRAPHICS', 'TITLE', 'QUALITY_CHECK', 'SEO', 'PUBLISHING', 'COMPLETED'];
    const currentIndex = stageOrder.indexOf(currentStage);
    const stepIndex = stageOrder.indexOf(stepStage);
    if (currentIndex === -1 || stepIndex === -1) return false;
    return currentIndex > stepIndex || currentStage === 'COMPLETED';
  }

  isStageActive(currentStage: string, stepStage: string): boolean {
    if (!currentStage) currentStage = 'EPISODE_CREATION';
    return currentStage === stepStage;
  }

  isMyNote(note: any): boolean {
    if (!note || note.userId === undefined || note.userId === null) return false;
    return Number(note.userId) === Number(this.loggedInUserId);
  }
}
